import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import {
  Radio as RadioIcon,
  Globe,
  Music,
  Play,
  Pause,
  Volume,
  Volume1,
  Volume2,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Moon,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { useElementOverrides } from "@/contexts/ElementOverrideContext";
import { useScanEngineContext } from "@/contexts/ScanEngineContext";
import { MediaRestMode } from "@/components/modals/MediaRestMode";

const getFlagUrl = (countryCode: string) =>
  `https://flagcdn.com/w80/${countryCode.toLowerCase()}.png`;

// Base URL for raw M3U files from GitHub (genres only)
const GITHUB_RAW_BASE = "https://raw.githubusercontent.com/junguler/m3u-radio-music-playlists/main";

// Small station logo with fallback to generic RadioIcon
function StationLogo({ favicon, name }: { favicon?: string; name: string }) {
  const [imgError, setImgError] = React.useState(false);
  if (favicon && !imgError) {
    return (
      <div className="flex-shrink-0 rounded-xl overflow-hidden bg-muted border border-border flex items-center justify-center" style={{ width: 'clamp(2rem, 3.5vw, 3rem)', height: 'clamp(2rem, 3.5vw, 3rem)' }}>
        <img
          src={favicon}
          alt={name}
          className="w-full h-full object-contain"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }
  return (
    <div className="flex-shrink-0 rounded-xl bg-muted border border-border flex items-center justify-center" style={{ width: 'clamp(2rem, 3.5vw, 3rem)', height: 'clamp(2rem, 3.5vw, 3rem)' }}>
      <RadioIcon className="w-4 h-4 lg:w-5 lg:h-5 text-primary/50" />
    </div>
  );
}

// Radio Browser API base URL
const RADIO_BROWSER_API = "https://de1.api.radio-browser.info/json/stations";

// Country data with flags and country codes for Radio Browser API
// Priority country IDs per language (first = top-start)
const COUNTRY_PRIORITY: Record<string, string[]> = {
  he: ["israel"],
  en: ["usa", "uk"],
  ru: ["russia", "israel"],
  ar: ["arabic", "israel"],
};

const ALL_COUNTRIES = (t: (key: string) => string) => [
  { id: "israel", name: t("israel"), flag: "🇮🇱", countryCode: "IL" },
  { id: "usa", name: t("usa"), flag: "🇺🇸", countryCode: "US" },
  { id: "uk", name: t("uk"), flag: "🇬🇧", countryCode: "GB" },
  { id: "france", name: t("franceCountry"), flag: "🇫🇷", countryCode: "FR" },
  { id: "germany", name: t("germanyCountry"), flag: "🇩🇪", countryCode: "DE" },
  { id: "spain", name: t("spainCountry"), flag: "🇪🇸", countryCode: "ES" },
  { id: "italy", name: t("italyCountry"), flag: "🇮🇹", countryCode: "IT" },
  { id: "russia", name: t("russiaCountry"), flag: "🇷🇺", countryCode: "RU" },
  { id: "brazil", name: t("brazilCountry"), flag: "🇧🇷", countryCode: "BR" },
  { id: "japan", name: t("japanCountry"), flag: "🇯🇵", countryCode: "JP" },
  { id: "india", name: t("indiaCountry"), flag: "🇮🇳", countryCode: "IN" },
  { id: "arabic", name: t("arabic"), flag: "🌍", countryCode: "SA" },
  { id: "greek", name: t("greeceCountry"), flag: "🇬🇷", countryCode: "GR" },
  { id: "turkish", name: t("turkeyCountry"), flag: "🇹🇷", countryCode: "TR" },
  { id: "polish", name: t("polandCountry"), flag: "🇵🇱", countryCode: "PL" },
];

const getCountries = (t: (key: string) => string, lang: string) => {
  const all = ALL_COUNTRIES(t);
  const priority = COUNTRY_PRIORITY[lang] || COUNTRY_PRIORITY.he;
  const prioritized = priority.map(id => all.find(c => c.id === id)).filter(Boolean);
  const rest = all.filter(c => !priority.includes(c.id));
  return [...prioritized, ...rest] as typeof all;
};

// Genre data with icons and M3U file names (for GitHub source)
const getGenres = (t: (key: string) => string) => [
  { id: "pop", name: t("popGenre"), icon: "🎵", file: "pop.m3u" },
  { id: "rock", name: t("rockGenre"), icon: "🎸", file: "rock.m3u" },
  { id: "jazz", name: t("jazzGenre"), icon: "🎷", file: "jazz.m3u" },
  { id: "classical", name: t("classicalGenre"), icon: "🎻", file: "classical.m3u" },
  { id: "electronic", name: t("electronicGenre"), icon: "🎧", file: "electronic.m3u" },
  { id: "hiphop", name: t("hiphopGenre"), icon: "🎤", file: "hip_hop.m3u" },
  { id: "country", name: t("countryGenre"), icon: "🤠", file: "country.m3u" },
  { id: "blues", name: t("bluesGenre"), icon: "🎺", file: "blues.m3u" },
  { id: "reggae", name: t("reggaeGenre"), icon: "🌴", file: "reggae.m3u" },
  { id: "ambient", name: t("ambientGenre"), icon: "🧘", file: "ambient.m3u" },
  { id: "oldies", name: t("oldiesGenre"), icon: "📻", file: "oldies.m3u" },
  { id: "80s", name: t("the80sGenre"), icon: "💿", file: "80s.m3u" },
  { id: "90s", name: t("the90sGenre"), icon: "📀", file: "90s.m3u" },
  { id: "dance", name: t("danceGenre"), icon: "💃", file: "dance.m3u" },
  { id: "soul", name: t("soulGenre"), icon: "❤️", file: "soul.m3u" },
  { id: "latin", name: t("latinGenre"), icon: "💃", file: "latin.m3u" },
  { id: "lounge", name: t("loungeGenre"), icon: "🍸", file: "lounge.m3u" },
  { id: "metal", name: t("metalGenre"), icon: "🤘", file: "metal.m3u" },
  { id: "alternative", name: t("alternativeGenre"), icon: "🎯", file: "alternative.m3u" },
  { id: "chillout", name: t("chilloutGenre"), icon: "😌", file: "chillout.m3u" },
];

interface Station {
  name: string;
  url: string;
  favicon?: string;
}

type CategoryType = "countries" | "genres";

export default function Radio() {
  const navigate = useNavigate();
  const { t, dir, lang, isRTL } = useTranslation();

  const countries = getCountries(t, lang);
  const genres = getGenres(t);
  const { getElementStyle } = useElementOverrides();
  const { isActive: isScanActive, focusDefaultItem } = useScanEngineContext();
  const isDarkModeGlobal = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const overrideMode = isDarkModeGlobal ? "dark" : "light";
  const countryOverride = getElementStyle("radio-country-card", overrideMode);
  const genreOverride = getElementStyle("radio-genre-card", overrideMode);
  const stationOverride = getElementStyle("radio-station-card", overrideMode);
  const [categoryType, setCategoryType] = useState<CategoryType>("countries");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentStation, setCurrentStation] = useState<Station | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [bufferingUrl, setBufferingUrl] = useState<string | null>(null);
  const [volume, setVolume] = useState(50);
  const [isMuted, setIsMuted] = useState(false);
  const [isMediaRestMode, setIsMediaRestMode] = useState(false);
  // Health check: map of station URL → is_healthy (true/false), undefined = not checked
  const [healthMap, setHealthMap] = useState<Map<string, boolean>>(new Map());
  const unhealthyUrls = useMemo(
    () => new Set([...healthMap.entries()].filter(([, v]) => v === false).map(([k]) => k)),
    [healthMap]
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [categoryPage, setCategoryPage] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const keepPlayingOnUnmountRef = useRef(false);
  const lastStationRef = useRef<Station | null>(null);
  const stationGridRef = useRef<HTMLDivElement | null>(null);   // on the inner grid area only (no arrows)
  const stationOuterRef = useRef<HTMLDivElement | null>(null);  // on the stable outer flex wrapper
  const categoryGridRef = useRef<HTMLDivElement | null>(null);
  const categoryWrapperRef = useRef<HTMLDivElement | null>(null);
  const [stationRows, setStationRows] = useState(5);
  const [categoryRows, setCategoryRows] = useState(3);

  const categories = categoryType === "countries" ? countries : genres;
  const COLS = 5;

  // Category pagination (no-scroll, fits viewport height)
  const CATEGORIES_PER_PAGE = COLS * categoryRows;
  const totalCategoryPages = Math.ceil(categories.length / CATEGORIES_PER_PAGE);
  const hasPrevCategoryPage = categoryPage > 0;
  const hasNextCategoryPage = categoryPage < totalCategoryPages - 1;
  const pageCategories = categories.slice(
    categoryPage * CATEGORIES_PER_PAGE,
    (categoryPage + 1) * CATEGORIES_PER_PAGE
  );
  
  // Station grid: 2 columns, dynamic rows based on available height
  const STATION_COLS = 2;
  const STATIONS_PER_PAGE = STATION_COLS * stationRows;
  
  // Dynamic sort: healthy (or unknown) stations first, unhealthy last
  const sortedStations = useMemo(() => {
    return [...stations].sort((a, b) => {
      const aHealthy = healthMap.get(a.url) !== false;
      const bHealthy = healthMap.get(b.url) !== false;
      if (aHealthy === bHealthy) return 0;
      return aHealthy ? -1 : 1;
    });
  }, [stations, healthMap]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedStations.length / STATIONS_PER_PAGE);
  const hasPrevPage = currentPage > 0;
  const hasNextPage = currentPage < totalPages - 1;
  const pageStations = sortedStations.slice(
    currentPage * STATIONS_PER_PAGE,
    (currentPage + 1) * STATIONS_PER_PAGE
  );

  useEffect(() => {
    if (!selectedCategory || loading || pageStations.length === 0 || !isScanActive) return;

    const rafId = requestAnimationFrame(() => {
      focusDefaultItem();
    });

    return () => cancelAnimationFrame(rafId);
  }, [selectedCategory, loading, currentPage, pageStations.length, isScanActive, focusDefaultItem]);

  // Dynamically calculate how many station rows fit — ref is on the INNER grid area only (not arrows)
  useEffect(() => {
    const container = stationGridRef.current;
    if (!container) return;
    const calculate = () => {
      const availableHeight = container.clientHeight;
      const ROW_MIN_HEIGHT = 52;
      const GAP = 8;
      const rows = Math.max(2, Math.min(12, Math.floor((availableHeight + GAP) / (ROW_MIN_HEIGHT + GAP))));
      setStationRows(rows);
    };
    calculate();
    const ro = new ResizeObserver(calculate);
    ro.observe(container);
    return () => ro.disconnect();
  }, [selectedCategory]);

  // Dynamically calculate how many category rows fit — measure the stable wrapper, not the grid itself
  useEffect(() => {
    const wrapper = categoryWrapperRef.current;
    if (!wrapper) return;
    const FIXED_ABOVE_GRID = 104; // tabs h-14 (56px) + mb-3 (12px) + label (24px) + mb-3 (12px)
    const ROW_MIN_HEIGHT = 72;
    const GAP = 12;
    const calculate = () => {
      const availableForGrid = wrapper.clientHeight - FIXED_ABOVE_GRID;
      const calculatedRows = Math.max(2, Math.floor((availableForGrid + GAP) / (ROW_MIN_HEIGHT + GAP)));
      const neededRows = Math.ceil(categories.length / COLS);
      setCategoryRows(Math.min(calculatedRows, neededRows));
    };
    calculate();
    const ro = new ResizeObserver(calculate);
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [categoryType, selectedCategory, categories.length]);
  
  const goBack = useCallback(() => {
    // goBack = close button inside the page → stop audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setCategoryRows(3);
    setCurrentStation(null);
    setIsPlaying(false);
    setSelectedCategory(null);
    setStations([]);
  }, []);

  // Fetch all station health statuses and build a Map<url, is_healthy>
  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const { data, error } = await supabase
          .from("radio_station_health")
          .select("station_url, is_healthy");
        if (!error && data) {
          setHealthMap(new Map(data.map((r: { station_url: string; is_healthy: boolean }) => [r.station_url, r.is_healthy])));
        }
      } catch {
        // Silent fail - don't block radio usage
      }
    };
    fetchHealth();
  }, []);

  // Parse M3U content to extract stations
  const parseM3U = (content: string): Station[] => {
    const lines = content.split('\n');
    const stationList: Station[] = [];
    let currentName = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith('#EXTINF:')) {
        const match = line.match(/,(.+)$/);
        if (match) {
          currentName = match[1].trim();
        }
      } else if (line && !line.startsWith('#') && (line.startsWith('http') || line.startsWith('https'))) {
        stationList.push({
          name: currentName || `Station ${stationList.length + 1}`,
          url: line
        });
        currentName = '';
      }
    }

    return stationList.slice(0, 50);
  };

  // Load stations for selected category
  const loadStations = async (categoryId: string) => {
    setLoading(true);
    setStations([]);
    
    if (categoryType === "countries") {
      const country = countries.find(c => c.id === categoryId);
      if (!country) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${RADIO_BROWSER_API}/bycountrycodeexact/${country.countryCode}?limit=50&order=clickcount&reverse=true`
        );
        if (response.ok) {
          const data = await response.json();
          const parsedStations: Station[] = data
            .filter((s: any) => s.url_resolved)
            .map((s: any) => ({
              name: s.name,
              url: s.url_resolved,
              favicon: s.favicon || undefined,
            }));

          // Custom ordering: swap KAN ISRAEL and GALEI ZAHAL positions
          const STATION_ORDER_OVERRIDES: Record<string, number> = {
            "galei zahal": 1,
            "kan": 2,
          };
          const getStationPriority = (name: string) => {
            const lower = name.toLowerCase();
            for (const [key, priority] of Object.entries(STATION_ORDER_OVERRIDES)) {
              if (lower.includes(key)) return priority;
            }
            return 99;
          };
          parsedStations.sort((a, b) => {
            const pa = getStationPriority(a.name);
            const pb = getStationPriority(b.name);
            if (pa !== pb) return pa - pb;
            return 0;
          });

          setStations(parsedStations);
        }
      } catch (error) {
        // Silent fail
      }
    } else {
      const genre = genres.find(g => g.id === categoryId);
      if (!genre) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${GITHUB_RAW_BASE}/${genre.file}`);
        if (response.ok) {
          const content = await response.text();
          const parsedStations = parseM3U(content);
          setStations(parsedStations);
        }
      } catch (error) {
        // Silent fail
      }
    }
    
    setLoading(false);
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setCurrentPage(0);
    loadStations(categoryId);
  };

  const playStation = (station: Station) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Show buffering indicator immediately on selection
    setBufferingUrl(station.url);

    const audio = new Audio(station.url);
    audio.volume = isMuted ? 0 : volume / 100;
    audioRef.current = audio;
    
    audio.play().then(() => {
      setCurrentStation(station);
      lastStationRef.current = station;
      setIsPlaying(true);
      setBufferingUrl(null);
    }).catch(() => {
      setBufferingUrl(null);
    });

    audio.onerror = () => {
      setBufferingUrl(null);
    };
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100;
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? volume / 100 : 0;
    }
  };


  useEffect(() => {
    return () => {
      if (!keepPlayingOnUnmountRef.current && audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);


  const selectedCategoryData = categories.find(c => c.id === selectedCategory);

  return (
    <MainLayout
      hideSidebar={isMediaRestMode}
      onBack={currentStation ? () => {
        // Sidebar back from now-playing: stop audio, return to station list
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        setCurrentStation(null);
        setIsPlaying(false);
      } : selectedCategory ? goBack : undefined}
    >
      <div className="flex-1 flex flex-col bg-background min-h-0 overflow-hidden relative" dir={dir}>
        {/* Full-screen buffering overlay — shown while a station is connecting */}
        <AnimatePresence>
          {bufferingUrl && (
            <motion.div
              key="buffering-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-background/90 backdrop-blur-sm"
            >
              <Loader2 className="w-16 h-16 text-primary animate-spin mb-4" />
              <p className="text-xl font-semibold text-foreground">
                {stations.find(s => s.url === bufferingUrl)?.name ?? bufferingUrl}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Header — hidden in now-playing fullscreen */}
        {!currentStation && (
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RadioIcon className="w-8 h-8 text-primary" />
                <h1 className="text-2xl font-bold text-foreground">{t("radio")}</h1>
              </div>
            </div>
          </div>
        )}

        {/* NOW PLAYING FULLSCREEN */}
        <AnimatePresence mode="wait">
          {currentStation ? (
            <motion.div
              key="now-playing"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              className="flex-1 flex flex-col items-center justify-between p-6 gap-4 overflow-hidden"
            >
              {/* Station artwork */}
              <div className="flex-1 flex flex-col items-center justify-center gap-6 min-h-0">
                <div className="w-48 h-48 rounded-3xl bg-muted flex items-center justify-center shadow-2xl overflow-hidden border-4 border-border">
                  {currentStation.favicon ? (
                    <img
                      src={currentStation.favicon}
                      alt={currentStation.name}
                      className="w-full h-full object-contain"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <RadioIcon className="w-24 h-24 text-primary/40" />
                  )}
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground text-lg font-medium mb-1">{t("nowPlaying")}</p>
                  <p className="text-foreground text-2xl font-bold leading-tight">{currentStation.name}</p>
                </div>
              </div>

               {/* Controls or Media Rest Mode */}
              {isMediaRestMode ? (
                <MediaRestMode isOpen={isMediaRestMode} onWake={() => setIsMediaRestMode(false)} />
              ) : (
               <div className="relative flex items-stretch gap-2 w-full px-2">
                {([
                  { onClick: togglePlayPause, icon: isPlaying ? <Pause className="w-5 h-5 text-primary relative z-10" /> : <Play className="w-5 h-5 text-primary relative z-10" />, label: isPlaying ? t("pause") : t("play"), active: false, accent: true },
                  { onClick: () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setCurrentStation(null); setIsPlaying(false); setIsMediaRestMode(false); }, icon: <X className="w-5 h-5 text-primary relative z-10" />, label: t("close"), active: false, accent: false },
                  { onClick: () => setIsMediaRestMode(true), icon: <Moon className="w-5 h-5 text-primary relative z-10" />, label: t("rest"), active: false, accent: false },
                  { onClick: () => handleVolumeChange([25]), icon: <Volume className={cn("w-5 h-5 relative z-10", volume === 25 && !isMuted ? "text-primary-foreground" : "text-primary")} />, label: t("low"), active: volume === 25 && !isMuted, accent: false },
                  { onClick: () => handleVolumeChange([50]), icon: <Volume1 className={cn("w-5 h-5 relative z-10", volume === 50 && !isMuted ? "text-primary-foreground" : "text-primary")} />, label: t("mediumVol"), active: volume === 50 && !isMuted, accent: false },
                  { onClick: () => handleVolumeChange([100]), icon: <Volume2 className={cn("w-5 h-5 relative z-10", volume === 100 && !isMuted ? "text-primary-foreground" : "text-primary")} />, label: t("high"), active: volume === 100 && !isMuted, accent: false },
                ] as const).map(({ onClick, icon, label, active }, idx) => (
                  <button
                    key={idx}
                    data-scan-item
                    onClick={onClick}
                    className={cn(
                      "relative flex flex-col items-center justify-center gap-1 py-3 px-2 rounded-xl flex-1 border-2 shadow-md transition-all duration-200 hover:border-primary hover:scale-[1.02] active:scale-[0.98]",
                      active ? "bg-primary border-primary text-primary-foreground" : "bg-card border-border",
                    )}
                  >
                    {icon}
                    <span className={cn("font-medium leading-none relative z-10", active ? "text-primary-foreground" : "text-card-foreground")} style={{ fontSize: 'clamp(1em, 2vh, 1.2em)' }}>{label}</span>
                  </button>
                ))}
              </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="browse"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col min-h-0 overflow-hidden"
            >
          {!selectedCategory ? (
            <div ref={categoryWrapperRef} className="flex-1 flex flex-col p-4 overflow-hidden" dir={dir}>
              {/* Tabs row */}
              <div data-scan-row="radio-tabs" className="relative flex gap-3 mb-3 flex-shrink-0 rounded-xl">
                {isRTL ? (
                  <>
                    <Button
                      data-scan-item
                      data-scan-cursor-target="radio-first-category"
                      variant={categoryType === "countries" ? "default" : "outline"}
                      onClick={() => { setCategoryType("countries"); setCategoryPage(0); }}
                      className={cn(
                        "gap-2 flex-1 h-14 radio-tab-font text-[length:inherit]",
                        categoryType !== "countries" && "bg-card border-border",
                      )}
                    >
                      <Globe className="w-5 h-5" />
                      {t("countriesLabel")}
                    </Button>
                    <Button
                      data-scan-item
                      data-scan-cursor-target="radio-first-category"
                      variant={categoryType === "genres" ? "default" : "outline"}
                      onClick={() => { setCategoryType("genres"); setCategoryPage(0); }}
                      className={cn(
                        "gap-2 flex-1 h-14 radio-tab-font text-[length:inherit]",
                        categoryType !== "genres" && "bg-card border-border",
                      )}
                    >
                      <Music className="w-5 h-5" />
                      {t("genresLabel")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      data-scan-item
                      data-scan-cursor-target="radio-first-category"
                      variant={categoryType === "countries" ? "default" : "outline"}
                      onClick={() => { setCategoryType("countries"); setCategoryPage(0); }}
                      className={cn(
                        "gap-2 flex-1 h-14 radio-tab-font text-[length:inherit]",
                        categoryType !== "countries" && "bg-card border-border",
                      )}
                    >
                      <Globe className="w-5 h-5" />
                      {t("countriesLabel")}
                    </Button>
                    <Button
                      data-scan-item
                      data-scan-cursor-target="radio-first-category"
                      variant={categoryType === "genres" ? "default" : "outline"}
                      onClick={() => { setCategoryType("genres"); setCategoryPage(0); }}
                      className={cn(
                        "gap-2 flex-1 h-14 radio-tab-font text-[length:inherit]",
                        categoryType !== "genres" && "bg-card border-border",
                      )}
                    >
                      <Music className="w-5 h-5" />
                      {t("genresLabel")}
                    </Button>
                  </>
                )}
              </div>

              {/* Category grid */}
              <div
                ref={categoryGridRef}
                className="flex-1 flex flex-col gap-3 min-h-0 overflow-hidden p-[5px]"
              >
                {Array.from({ length: Math.ceil(pageCategories.length / COLS) }, (_, rowIdx) => {
                  const rowStart = rowIdx * COLS;
                  const rowItems = pageCategories.slice(rowStart, rowStart + COLS);
                  return (
                    <div
                      key={rowIdx}
                      data-scan-row={`radio-cat-${rowIdx}`}
                      className="relative flex-1 min-h-0 rounded-xl"
                      style={{ display: 'grid', gridTemplateColumns: `repeat(${COLS}, 1fr)`, gap: '12px' }}
                    >
                      {rowItems.map((category, catIdx) => (
                          <button
                            key={category.id}
                            data-scan-item
                            {...(rowIdx === 0 && catIdx === 0 ? { 'data-scan-default': '', 'data-scan-cursor-id': 'radio-first-category', 'data-scan-cursor-level': 'row' } : {})}
                            data-design-id={categoryType === "countries" ? "radio-country-card" : "radio-genre-card"}
                            onClick={() => handleCategorySelect(category.id)}
                            className="relative flex flex-col items-center justify-center gap-1 rounded-xl bg-card shadow-sm hover:bg-primary/10 transition-all w-full h-full min-h-0 border border-[#1E888F]"
                            style={{
                              ...((() => {
                                const ov = categoryType === "countries" ? countryOverride : genreOverride;
                                if (!ov) return {};
                                const s: React.CSSProperties = {};
                                if (ov.backgroundColor) s.backgroundColor = ov.backgroundColor;
                                if (ov.textColor) s.color = ov.textColor;
                                if (ov.borderColor) s.borderColor = ov.borderColor;
                                if (ov.borderWidth) { s.borderWidth = `${ov.borderWidth}px`; s.borderStyle = "solid"; }
                                return s;
                              })()),
                            }}
                          >
                            {'countryCode' in category && category.id !== 'arabic' ? (
                              <img
                                src={getFlagUrl(category.countryCode)}
                                alt={category.name}
                                className="w-10 h-7 object-cover rounded shadow-sm relative z-10"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ) : (
                              <span className="relative z-10" style={{ fontSize: 'clamp(1.2rem, 3vh, 1.8rem)' }}>
                                {'flag' in category ? category.flag : category.icon}
                              </span>
                            )}
                            <span className="font-medium leading-tight text-center relative z-10" style={{ fontSize: 'clamp(1.1em, 2.5vh, 1.5em)' }}>
                              {category.name}
                            </span>
                          </button>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Category pagination (only if needed) */}
              {totalCategoryPages > 1 && (
                <div className="flex gap-2 mt-3 flex-shrink-0" data-scan-row="pagination">
                  <button
                    {...(hasPrevCategoryPage ? { 'data-scan-item': '' } : {})}
                    onClick={() => hasPrevCategoryPage && setCategoryPage(p => p - 1)}
                    className={cn(
                      "flex-1 h-10 rounded-xl border-2 flex items-center justify-center transition-all",
                      hasPrevCategoryPage
                        ? "bg-card border-primary/40 hover:bg-primary/10 cursor-pointer"
                        : "border-transparent bg-transparent cursor-default opacity-0",
                    )}
                  >
                    {hasPrevCategoryPage && (isRTL ? <ChevronRight className="w-5 h-5 text-primary" strokeWidth={3} /> : <ChevronLeft className="w-5 h-5 text-primary" strokeWidth={3} />)}
                  </button>
                  <span className="text-sm text-muted-foreground flex items-center">{categoryPage + 1} / {totalCategoryPages}</span>
                  <button
                    {...(hasNextCategoryPage ? { 'data-scan-item': '' } : {})}
                    onClick={() => hasNextCategoryPage && setCategoryPage(p => p + 1)}
                    className={cn(
                      "flex-1 h-10 rounded-xl border-2 flex items-center justify-center transition-all",
                      hasNextCategoryPage
                        ? "bg-card border-primary/40 hover:bg-primary/10 cursor-pointer"
                        : "border-transparent bg-transparent cursor-default opacity-0",
                    )}
                  >
                    {hasNextCategoryPage && (isRTL ? <ChevronLeft className="w-5 h-5 text-primary" strokeWidth={3} /> : <ChevronRight className="w-5 h-5 text-primary" strokeWidth={3} />)}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col p-4 min-h-0 overflow-hidden" dir={dir}>
              {/* Category header */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">
                  {selectedCategoryData && (
                    'countryCode' in selectedCategoryData && selectedCategoryData.id !== 'arabic' ? (
                      <img
                        src={getFlagUrl(selectedCategoryData.countryCode)}
                        alt={selectedCategoryData.name}
                        className="w-10 h-7 object-cover rounded shadow-sm inline-block"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      'flag' in selectedCategoryData ? selectedCategoryData.flag : selectedCategoryData.icon
                    )
                  )}
                </span>
                <h2 className="text-xl font-bold text-foreground">
                  {selectedCategoryData?.name}
                </h2>
                {totalPages > 1 && (
                  <span className="text-sm text-muted-foreground ms-auto">
                    {currentPage + 1} / {totalPages}
                  </span>
                )}
              </div>

              {loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : stations.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  {t("noStationsFound")}
                </div>
              ) : (
                <div ref={stationOuterRef} className="flex-1 flex items-stretch gap-2 min-h-0 min-w-0">
                  {/* Prev arrow */}
                  <div className="flex-shrink-0 w-12 h-full" data-scan-row="pagination-prev">
                  <button
                    {...(hasPrevPage ? { 'data-scan-item': '' } : {})}
                    onClick={() => hasPrevPage && setCurrentPage(p => p - 1)}
                    className={cn(
                      "flex items-center justify-center w-12 h-full rounded-xl border-2 shadow-sm transition-all",
                      hasPrevPage
                        ? "bg-card border-primary/40 hover:bg-primary/10 cursor-pointer"
                        : "border-transparent bg-transparent shadow-none cursor-default",
                    )}
                  >
                    {hasPrevPage && (isRTL ? <ChevronRight className="w-6 h-6 text-primary" strokeWidth={3} /> : <ChevronLeft className="w-6 h-6 text-primary" strokeWidth={3} />)}
                  </button>
                  </div>

                  {/* Station grid */}
                  <div ref={stationGridRef} className="flex-1 min-h-0 overflow-hidden flex flex-col gap-2 p-[5px]">
                    {Array.from({ length: stationRows }, (_, rowIdx) => {
                      const rowStart = rowIdx * STATION_COLS;
                      const rowItems = pageStations.slice(rowStart, rowStart + STATION_COLS);
                      if (rowItems.length === 0) return null;
                      return (
                        <div
                          key={rowIdx}
                          data-scan-row={`radio-station-${rowIdx}`}
                          className="relative flex-1 min-h-0 rounded-xl grid gap-2"
                          style={{ gridTemplateColumns: `repeat(${STATION_COLS}, 1fr)` }}
                        >
                          {rowItems.map((station, colIdx) => {
                            const isStationActive = currentStation?.url === station.url;
                            const isActiveAndPlaying = isStationActive && isPlaying;
                            const isBufferingThis = bufferingUrl === station.url;
                            const isUnhealthy = unhealthyUrls.has(station.url);
                            return (
                              <button
                                key={`${currentPage}-${rowStart + colIdx}`}
                                data-scan-item
                                {...(rowIdx === 0 && colIdx === 0 ? { 'data-scan-default': '', 'data-scan-cursor-level': 'row' } : {})}
                                data-design-id="radio-station-card"
                                onClick={() => !isUnhealthy && (isStationActive ? togglePlayPause() : playStation(station))}
                                disabled={isUnhealthy}
                                className={cn(
                                  "relative rounded-xl transition-all flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-3 min-h-0 h-full min-w-0 w-full border-2 shadow-sm",
                                  isUnhealthy
                                    ? "opacity-40 cursor-not-allowed bg-muted border-border"
                                    : "hover:bg-primary/10 border-border",
                                  !isUnhealthy && (isStationActive || isBufferingThis)
                                    ? "bg-primary/20"
                                    : !isUnhealthy && "bg-card",
                                )}
                                style={{
                                  ...(!isUnhealthy && stationOverride ? (() => {
                                    const s: React.CSSProperties = {};
                                    if (stationOverride.backgroundColor) s.backgroundColor = stationOverride.backgroundColor;
                                    if (stationOverride.textColor) s.color = stationOverride.textColor;
                                    if (stationOverride.borderColor) s.borderColor = stationOverride.borderColor;
                                    if (stationOverride.borderWidth) { s.borderWidth = `${stationOverride.borderWidth}px`; s.borderStyle = "solid"; }
                                    return s;
                                  })() : {}),
                                }}
                              >
                                <StationLogo favicon={station.favicon} name={station.name} />
                                <span className={cn(
                                  "flex-1 font-medium truncate text-start",
                                  isUnhealthy ? "text-muted-foreground line-through" : "text-foreground"
                                )}>
                                  {station.name}
                                </span>
                                <div className={cn(
                                  "flex-shrink-0 flex items-center gap-1.5 rounded-full font-semibold transition-all px-3 lg:px-4",
                                  "h-8 lg:h-9",
                                  isUnhealthy
                                    ? "bg-muted text-muted-foreground"
                                    : isStationActive || isBufferingThis
                                      ? "bg-primary text-primary-foreground"
                                      : "bg-muted/50 text-foreground"
                                )} style={{ fontSize: 'clamp(1em, 2vh, 1.2em)' }}>
                                  {isBufferingThis ? (
                                    <>
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      <span>...</span>
                                    </>
                                  ) : isActiveAndPlaying ? (
                                    <>
                                      <Pause className="w-4 h-4" />
                                      <span>{t("pause")}</span>
                                    </>
                                  ) : (
                                    <>
                                      <Play className="w-4 h-4" />
                                      <span>{isUnhealthy ? "—" : t("play")}</span>
                                    </>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>

                  {/* Next arrow */}
                  <div className="flex-shrink-0 w-12 h-full" data-scan-row="pagination-next">
                  <button
                    {...(hasNextPage ? { 'data-scan-item': '' } : {})}
                    onClick={() => hasNextPage && setCurrentPage(p => p + 1)}
                    className={cn(
                      "flex items-center justify-center w-12 h-full rounded-xl border-2 shadow-sm transition-all",
                      hasNextPage
                        ? "bg-card border-primary/40 hover:bg-primary/10 cursor-pointer"
                        : "border-transparent bg-transparent shadow-none cursor-default",
                    )}
                  >
                  {hasNextPage && (isRTL ? <ChevronLeft className="w-6 h-6 text-primary" strokeWidth={3} /> : <ChevronRight className="w-6 h-6 text-primary" strokeWidth={3} />)}
                  </button>
                  </div>
              </div>
            )}
          </div>
        )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MainLayout>
  );
}
